import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { ContentRegistry } from '../content/registry';
import type { VisualDef } from '../content/types';
import { primitiveGeometry, toonMaterial } from './geometry';

/**
 * GLTF DROP-IN PIPELINE.
 * ----------------------
 * Content authors set `VisualDef.gltf` to a path under `public/models/` (see
 * `public/models/README.md` for where to get CC0 models and authoring
 * conventions). `ModelLibrary.preload()` loads every distinct path referenced
 * by the current content, merges each model's meshes into ONE flat-shaded,
 * non-indexed `BufferGeometry` (position + normal + baked per-vertex color,
 * plus UVs when every textured mesh shares one atlas), and normalizes it to the same convention every
 * primitive in `geometry.ts` uses: it fits inside a sphere of radius 0.5
 * centred at the local origin, with its lowest point (feet) at y≈-0.5 — so a
 * loaded model drops into `instanced.ts`'s existing scale/position math
 * (`scale = visual.scale * 2 * radius`, `y = radius * scaleY * 0.9`)
 * unchanged.
 *
 * A path that fails to load (404, parse error, ...) logs one `console.warn`
 * and is simply absent from the map; every consumer falls back to the
 * primitive shape via `geometryFor()`/`has()`. Nothing here ever throws.
 *
 * `models/player.glb` is a reserved legacy fallback for the player avatar.
 * Playable characters with skeletons/animations use `render/characters.ts`
 * instead, because flattening them here would discard their rig and clips.
 */
export const PLAYER_MODEL_PATH = 'models/player.glb';

/** Mirrors the guard in `meta/backend.ts` — this file must also work in the
 * plain-ESM playtest build (`tools/build-esm.sh`), which has no Vite and no
 * `import.meta.env`. */
function baseUrl(): string {
  try {
    const env = (import.meta as { env?: { BASE_URL?: string } }).env;
    return env?.BASE_URL ?? './';
  } catch {
    return './';
  }
}

function resolveUrl(path: string): string {
  const base = baseUrl();
  return (base.endsWith('/') ? base : base + '/') + path.replace(/^\/+/, '');
}

/** Merges every mesh under `root` (world-space) into one non-indexed geometry
 * with position/normal/color, then normalizes it (see module doc). Returns
 * `null` if the model has no mesh geometry at all. */
export interface ModelAsset {
  geometry: THREE.BufferGeometry;
  /** Shared diffuse atlas retained from the GLTF. One map keeps the model at one draw call. */
  map?: THREE.Texture;
}

export function buildModelAsset(root: THREE.Object3D): ModelAsset | null {
  root.updateWorldMatrix(true, true);
  const rawPos: number[] = [];
  const rawNormal: number[] = [];
  const rawColor: number[] = [];
  const rawUv: number[] = [];
  const v = new THREE.Vector3();
  const n = new THREE.Vector3();
  const normalMatrix = new THREE.Matrix3();
  const vc = new THREE.Color();
  const box = new THREE.Box3();
  let sharedMap: THREE.Texture | undefined;
  let sawTextured = false;
  let sawUntextured = false;
  let textureCompatible = true;

  root.traverse((obj: THREE.Object3D) => {
    const mesh = obj as THREE.Mesh;
    if (!(mesh as unknown as { isMesh?: boolean }).isMesh) return;
    let geo = mesh.geometry;
    if (!geo || !geo.attributes.position) return;
    if (geo.index) geo = geo.toNonIndexed();
    const posAttr = geo.attributes.position as THREE.BufferAttribute;
    const normAttr = geo.attributes.normal as THREE.BufferAttribute | undefined;
    const colorAttr = geo.attributes.color as THREE.BufferAttribute | undefined;
    const uvAttr = geo.attributes.uv as THREE.BufferAttribute | undefined;
    normalMatrix.getNormalMatrix(mesh.matrixWorld);
    const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const standard = mat as THREE.MeshStandardMaterial | undefined;
    const baseColor = standard?.color ?? null;
    const map = standard?.map ?? undefined;
    if (map) {
      sawTextured = true;
      if (!uvAttr) textureCompatible = false;
      if (!sharedMap) sharedMap = map;
      else if (sharedMap !== map && sharedMap.source !== map.source) textureCompatible = false;
    } else sawUntextured = true;
    for (let i = 0; i < posAttr.count; i++) {
      v.fromBufferAttribute(posAttr, i).applyMatrix4(mesh.matrixWorld);
      rawPos.push(v.x, v.y, v.z);
      box.expandByPoint(v);
      if (normAttr) n.fromBufferAttribute(normAttr, i).applyMatrix3(normalMatrix).normalize();
      else n.set(0, 1, 0);
      rawNormal.push(n.x, n.y, n.z);
      if (colorAttr) vc.setRGB(colorAttr.getX(i), colorAttr.getY(i), colorAttr.getZ(i));
      else if (baseColor) vc.copy(baseColor);
      else vc.set(0xffffff);
      rawColor.push(vc.r, vc.g, vc.b);
      rawUv.push(uvAttr?.getX(i) ?? 0, uvAttr?.getY(i) ?? 0);
    }
  });
  if (rawPos.length === 0) return null;

  // Normalize: XZ centred on the bbox centre, Y so the lowest point is 0,
  // then uniformly scaled so the whole model fits a sphere of radius 0.5
  // around the bbox centre — finally shifted down 0.5 so feet land at -0.5.
  const center = box.getCenter(new THREE.Vector3());
  const minY = box.min.y;
  let maxDist = 1e-6;
  for (let i = 0; i < rawPos.length; i += 3) {
    const dx = rawPos[i] - center.x;
    const dy = rawPos[i + 1] - center.y;
    const dz = rawPos[i + 2] - center.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d > maxDist) maxDist = d;
  }
  const scale = 0.5 / maxDist;
  const positions = new Float32Array(rawPos.length);
  for (let i = 0; i < rawPos.length; i += 3) {
    positions[i] = (rawPos[i] - center.x) * scale;
    positions[i + 1] = (rawPos[i + 1] - minY) * scale - 0.5;
    positions[i + 2] = (rawPos[i + 2] - center.z) * scale;
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(rawNormal, 3));
  out.setAttribute('color', new THREE.Float32BufferAttribute(rawColor, 3));
  if (sawTextured && !sawUntextured && textureCompatible && sharedMap) {
    out.setAttribute('uv', new THREE.Float32BufferAttribute(rawUv, 2));
    sharedMap.colorSpace = THREE.SRGBColorSpace;
    return { geometry: out, map: sharedMap };
  }
  return { geometry: out };
}

export class ModelLibrary {
  private assets = new Map<string, ModelAsset>();
  private loader = new GLTFLoader();

  /** True once `path` loaded successfully (post-`preload`). */
  has(path: string): boolean {
    return this.assets.has(path);
  }

  /** The merged geometry for a loaded path, or `undefined` if not loaded. */
  get(path: string): THREE.BufferGeometry | undefined {
    return this.assets.get(path)?.geometry;
  }

  /** Whether a loaded model retained a shared diffuse atlas. */
  hasTexture(path: string): boolean {
    return !!this.assets.get(path)?.map;
  }

  /** A toon material that preserves a model's atlas and baked color tint. */
  materialFor(visual: VisualDef, color = visual.color): THREE.MeshToonMaterial {
    const asset = visual.gltf ? this.assets.get(visual.gltf) : undefined;
    const mat = toonMaterial(asset ? 0xffffff : color, visual.emissive ?? 0, visual.emissiveIntensity ?? 0);
    if (asset) {
      mat.vertexColors = true;
      mat.map = asset.map ?? null;
      mat.needsUpdate = true;
    }
    return mat;
  }

  /** The model geometry for `visual.gltf` when loaded, else the matching primitive. */
  geometryFor(visual: VisualDef): THREE.BufferGeometry {
    if (visual.gltf) {
      const asset = this.assets.get(visual.gltf);
      if (asset) return asset.geometry;
    }
    return primitiveGeometry(visual.geo);
  }

  /** Loads every distinct `visual.gltf` path referenced by the registry (enemies,
   * bosses, era props, equipment) plus the reserved player model path. Never throws. */
  async preload(reg: ContentRegistry, onProgress?: (loaded: number, total: number) => void): Promise<void> {
    const paths = new Set<string>();
    for (const e of reg.enemyList) if (e.visual.gltf) paths.add(e.visual.gltf);
    for (const b of reg.bosses.values()) if (b.visual.gltf) paths.add(b.visual.gltf);
    for (const era of reg.eras) for (const p of era.environment.props) if (p.visual.gltf) paths.add(p.visual.gltf);
    for (const item of reg.itemList) if (item.visual?.gltf) paths.add(item.visual.gltf);
    paths.add(PLAYER_MODEL_PATH);

    const total = paths.size;
    let loaded = 0;
    await Promise.all(
      [...paths].map(async (path) => {
        try {
          const gltf = await this.loader.loadAsync(resolveUrl(path));
          const asset = buildModelAsset(gltf.scene);
          if (asset) this.assets.set(path, asset);
          else console.warn(`ModelLibrary: ${path} has no mesh geometry`);
        } catch (e) {
          console.warn(`ModelLibrary: failed to load ${path}`, e);
        } finally {
          loaded++;
          onProgress?.(loaded, total);
        }
      }),
    );
    const textured = [...this.assets.values()].filter((asset) => asset.map).length;
    console.info(`ModelLibrary: loaded ${this.assets.size}/${total} gltf model(s), ${textured} textured atlas model(s)`);
  }
}
